import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';

/**
 * Idle timeout hook - signs out user after period of inactivity
 * @param timeoutMs - Timeout in milliseconds (default: 15 minutes)
 */
export const useIdleTimeout = (timeoutMs: number = 15 * 60 * 1000) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Skip in development mode, or when disabled (timeoutMs <= 0, e.g. on /login during auth challenges)
    if (import.meta.env.DEV || !import.meta.env.VITE_COGNITO_USER_POOL_ID || timeoutMs <= 0) {
      return;
    }

    let timer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        console.log('[IDLE TIMEOUT] User inactive for', timeoutMs / 1000, 'seconds - signing out');
        await authService.signOut();
        navigate('/login');
      }, timeoutMs);
    };

    // Events that indicate user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    events.forEach(event => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    // Start the timer
    resetTimer();

    // Cleanup
    return () => {
      clearTimeout(timer);
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [timeoutMs, navigate]);
};
